import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "../../i18n";
import { IconButton } from "../Button/IconButton";
import { Skeleton } from "../Skeleton";
import { usePaginationContext } from "./context";
import { usePaginationRange } from "./hooks/usePaginationRange";

export const PageControls = () => {
  const { t } = useTranslation();
  const { page, pageSize, totalItems, onPageChange, loading, disabled } =
    usePaginationContext();
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginationRange = usePaginationRange(page, totalPages);

  const handlePreviousPage = useCallback(() => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  }, [page, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  }, [page, totalPages, onPageChange]);

  const handlePageClick = useCallback(
    (pageNumber: number) => {
      onPageChange(pageNumber);
    },
    [onPageChange],
  );

  if (loading) {
    return <Skeleton className="h-6 w-[248px]" />;
  }

  return (
    <nav
      aria-label={t("pagination.pagination")}
      className="flex items-center gap-1"
    >
      <IconButton
        size="xs"
        variant="ghost"
        color="neutral"
        onClick={handlePreviousPage}
        disabled={disabled || page === 1}
        aria-label={t("pagination.goToPrevPage")}
      >
        <ChevronLeft className="rtl:-scale-x-100" size={14} />
      </IconButton>

      {paginationRange.map((item, index) => {
        if (item === "...") {
          return (
            <span
              key={`ellipsis-${index}`}
              className="w-6 h-6 flex items-center justify-center text-neutral-500"
              aria-hidden="true"
            >
              {t("pagination.ellipsis")}
            </span>
          );
        }

        return (
          <IconButton
            key={item}
            size="xs"
            variant={page === item ? "filled" : "ghost"}
            color={page === item ? "primary" : "neutral"}
            onClick={() => handlePageClick(item)}
            disabled={disabled}
            aria-label={t("pagination.goToPage", { pageNumber: item })}
            aria-current={page === item ? "page" : undefined}
            className="transition-none"
          >
            {item}
          </IconButton>
        );
      })}

      <IconButton
        size="xs"
        variant="ghost"
        color="neutral"
        onClick={handleNextPage}
        disabled={disabled || page === totalPages}
        aria-label={t("pagination.goToNextPage")}
      >
        <ChevronRight className="rtl:-scale-x-100" size={14} />
      </IconButton>
    </nav>
  );
};
